import { useEffect, useState } from 'react';

/**
 * Hook to calculate a scale factor for viewport-based scaling
 * Designed to prevent overflow on smaller screens (like Chromecast)
 * 
 * @param targetWidth - Target design width (default: 1920)
 * @param targetHeight - Target design height (default: 1080)
 * @param minScale - Minimum scale factor (default: 0.1)
 * @param maxScale - Maximum scale factor (default: 1.0)
 * @returns Scale factor to apply via CSS transform
 */
export const useViewportScale = (
  targetWidth: number = 1920,
  targetHeight: number = 1080,
  minScale: number = 0.1,
  maxScale: number = 1.0
): number => {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const calculateScale = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate scale based on both width and height
      // Use the smaller scale to ensure everything fits
      const widthScale = viewportWidth / targetWidth;
      const heightScale = viewportHeight / targetHeight;
      const calculatedScale = Math.min(widthScale, heightScale);

      // Clamp the scale between min and max
      const clampedScale = Math.max(
        minScale,
        Math.min(maxScale, calculatedScale)
      );

      setScale(clampedScale);
    };

    // Calculate initial scale
    calculateScale();

    // Recalculate on resize
    window.addEventListener('resize', calculateScale);
    window.addEventListener('orientationchange', calculateScale);

    return () => {
      window.removeEventListener('resize', calculateScale);
      window.removeEventListener('orientationchange', calculateScale);
    };
  }, [targetWidth, targetHeight, minScale, maxScale]);

  return scale;
};
